import {
  FunctionComponent,
  Fragment,
  useState,
  useContext,
  useCallback,
} from 'react'
import config from '@config'
import CommentingItemList from './CommentingItemList'
import CommentingWrapper from './CommentingWrapper'
import Comment from './Comment'
import translate from '@i18n'
import Title from './Title'
import styled from 'styled-components'
import AppNexus from '@components/AppNexus'
import CommentingInfoBox from './CommentingInfoBox'
import PostedComment from './PostedComment'
import CommentingActionPopup from './CommentingActionPopup'
import CommentingPromptWrapper from './CommentingPromptWrapper'
import CommentingPrompt from './CommentingPrompt'
import APIContext from '@contexts/commenting/apiContext'
import { Comment as CommentType } from './types'
import GenericContext from '@contexts/commenting/genericContext'
import AnswerApiContextProvider from '@contexts/commenting/answerApiContextProvider'
import TopComments from './TopComments'
import TopCommentAPIContextProvider from '@contexts/commenting/topCommentApiContextProvider'
import { stripHtml } from '@hooks/useTracking/utils'
import useTracking, { TrackingFnType } from '@hooks/useTracking'
import usePageMetadata from '@hooks/usePageMetadata'

const {
  community: { commentsPerPage },
} = config

const ItemAdTitle = styled.div`
  font-weight: normal;
  font-size: 10px;
  color: ${({ theme }) => theme.colors.grey.grey_70};
  text-transform: uppercase;
  letter-spacing: 1.5px;
  text-align: center;
  margin: 20px 0 4px;
`

const CommentingWidget: FunctionComponent = () => {
  const { comments, fetchNextPage, moreToShow, postedComment, postComment } =
    useContext(APIContext)
  const { articleId, postingEnabled } = useContext(GenericContext)
  const [postingComment, setPostingComment] = useState(false)
  const { commentAd } = usePageMetadata()
  const articleClickMoreCommentHandler = useCallback<TrackingFnType>(
    ({ pageMetadata }) => {
      return {
        event: 'article_click_more_comment',
        eventCategory: 'article',
        eventLabel: `${pageMetadata.id}:${stripHtml(pageMetadata.title)}`,
        eventAction: 'more_comment',
      }
    },
    []
  )

  const handleArticleClickMoreCommentTracking = useTracking(
    articleClickMoreCommentHandler
  )

  const hasComments = comments?.length > 0

  const onShowMore = () => {
    if (moreToShow) {
      handleArticleClickMoreCommentTracking()
      fetchNextPage()
    }
  }

  return (
    <CommentingWrapper>
      <TopCommentAPIContextProvider>
        <TopComments />
      </TopCommentAPIContextProvider>
      <Title>{translate('commenting.title.all')}</Title>
      {!postingEnabled && !hasComments && <CommentingInfoBox />}
      {postedComment && <PostedComment comment={postedComment} />}
      {postingEnabled && !postingComment && !postedComment && (
        <CommentingPromptWrapper type="comment">
          <CommentingPrompt
            type="comment"
            onClick={() => setPostingComment(true)}
          />
        </CommentingPromptWrapper>
      )}
      {postingComment && (
        <CommentingActionPopup
          type="comment"
          actionType="reply"
          onConfirm={(textValue = '') => {
            setPostingComment(false)
            postComment({ body: textValue, articleId })
          }}
          onCancel={() => setPostingComment(false)}
        />
      )}
      {hasComments && (
        <CommentingItemList
          type="comment"
          moreToShow={moreToShow}
          onShowMore={onShowMore}
          rerenderKey={articleId}>
          {(comments as CommentType[])?.map((comment, index) => {
            const mappedCommentPage = Math.floor(index / commentsPerPage)
            const isFirstPage = mappedCommentPage === 0
            const isFirstCommentOnPage = index % commentsPerPage === 0

            return (
              <Fragment key={`comment-${comment.id}`}>
                <AnswerApiContextProvider answers={comment.answers}>
                  <Comment postingEnabled={postingEnabled} comment={comment} />
                </AnswerApiContextProvider>
                {!isFirstPage &&
                  isFirstCommentOnPage &&
                  commentAd?.placement &&
                  (commentAd?.slotCode || commentAd?.slotCodeMobile) && (
                    <>
                      <ItemAdTitle>{translate('adsTitle')}</ItemAdTitle>
                      <AppNexus {...commentAd} containerId={`${index}`} />
                    </>
                  )}
              </Fragment>
            )
          })}
        </CommentingItemList>
      )}
    </CommentingWrapper>
  )
}

export default CommentingWidget
